import { Flex, Skeleton, useToast } from '@chakra-ui/react';
import { Form, Formik, FormikProps } from 'formik';
import React, { useCallback, useState } from 'react';
import { Navigate, useParams, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { isWipModified } from '@finley-technologies/credit-agreement-utils';

import { CreditAgreementsViewTemplate } from '../CreditAgreementsViewTemplate';
import { useGetBankAccountsWithInstitutionAndOwnerByImplementationIdQuery } from 'packages/bankAccounts/requests/bankAccounts.gql.generated';
import { RouteErrorBoundary } from '~/contexts/MonitoringProvider';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { errorToastConfig } from '~/packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from '~/packages/common-frontend/utils';
import { CreditAgreementContextProvider } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/hooks';
import { useSaveClientWipCreditAgreement } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/hooks/useSaveClientWipCreditAgreement';
import { CreditAgreementWipHeader } from '~/packages/creditAgreements/ui/creditAgreementWip';
import { CreditAgreementFullArtifactWithIntrospectionEntityFetch } from '~/packages/creditAgreements/ui/creditAgreementWip/CreditAgreementFullArtifactWithIntrospectionEntityFetch';
import { createOrganizationMap } from '~/packages/creditAgreements/ui/creditAgreementWip/utils/createMaps';
import { useGetCommonWipData } from '~/packages/creditAgreements/ui/creditAgreementWip/utils/useGetCommonWipData';
import { useGetCurrentCaAndWip } from '~/packages/creditAgreements/ui/creditAgreementWip/utils/useGetCurrentCaAndWip';
import { createWipSchemaValidation } from '~/packages/creditAgreements/ui/creditAgreementWip/utils/wipSchemaValidation';
import { CreditAgreement } from '~/packages/creditAgreements/ui/types';
import { useVisibleOrganizationsQuery } from '~/packages/implementations/requests/implementations.gql.generated';

// Types
const RouteParamsStruct = z.object({
  implementationId: z.string({ message: 'Missing URL parameter' }),
  creditFacilityId: z.string({ message: 'Missing URL parameter' }),
});

// Client-safe full credit agreement WIP component using introspection
const CreditAgreementDocumentWipIntrospection = ({
  creditAgreementFullArtifactWip,
  currentCreditAgreementFullArtifact,
  organizationId,
  creditFacilityId,
}: {
  creditAgreementFullArtifactWip: CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
  currentCreditAgreementFullArtifact: CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
  organizationId: string;
  creditFacilityId: string;
}) => {
  const toast = useToast();
  const navigate = useNavigate();
  const params = useParams();
  const { implementationId } = RouteParamsStruct.parse(params);
  const [arePartiesLocked, setArePartiesLocked] = useState(false);
  const [isDiffOpen, setIsDiffOpen] = useState<boolean>(false);
  const [isJsonUploadOpen, setIsJsonUploadOpen] = useState(false);

  const {
    saveCreditAgreementFullArtifactWip,
    isSaveCreditAgreementFullArtifactWipLoading,
  } = useSaveClientWipCreditAgreement({ creditFacilityId });

  const { loanProductsMap, tAccounts, facilityType } = useGetCommonWipData({
    creditFacilityId,
  });

  const visibleOrganizationsQuery = useVisibleOrganizationsQuery({
    variables: { organizationId },
  });

  const {
    data: bankAccountsWithInstitutionsData,
  } = useGetBankAccountsWithInstitutionAndOwnerByImplementationIdQuery({
    variables: { implementationId },
  });

  const isWipDirtyCallback = useCallback(
    (newWip: CreditAgreement.DomainEntity.CreditAgreementFullArtifact) => {
      return isWipModified(creditAgreementFullArtifactWip, newWip);
    },
    [creditAgreementFullArtifactWip],
  );

  const onCommitDone = () => {
    const url = `/implementations/${implementationId}/credit-facilities/${creditFacilityId}/credit-agreements`;
    navigate(url);
  };

  // Early return if required data is not available
  if (
    !tAccounts ||
    !facilityType ||
    !loanProductsMap ||
    !visibleOrganizationsQuery.data ||
    !bankAccountsWithInstitutionsData?.implementation
      .bankAccountsWithInstitutionAndOwner
  ) {
    return <Skeleton height="150px" />;
  }

  // Create organization map using introspection (from existing WIP data)
  const parties = creditAgreementFullArtifactWip.facilityMechanics.parties;
  const allParties = [
    ...(parties.borrowers || []),
    ...(parties.capitalProviders || []),
    ...(parties.servicers || []),
    ...(parties.adminAgents || []),
    ...(parties.backupServicers || []),
    ...(parties.collectionAccountBanks || []),
    ...(parties.payingAgents || []),
    ...(parties.verificationAgents || []),
  ].filter(Boolean); // Remove any undefined/null entries
  const organizations = allParties
    .filter((party) => party && party.entity)
    .map((party) => party.entity);

  // also include all the orgs from visibleOrganizations in the CA org map
  // createOrganizationMap will handle any dups
  visibleOrganizationsQuery.data.organization.visibleOrganizations.forEach(
    (org) => {
      organizations.push(org);
    },
  );

  const organizationMap = createOrganizationMap(organizations);

  return (
    <Formik
      enableReinitialize
      initialValues={creditAgreementFullArtifactWip}
      validationSchema={createWipSchemaValidation()}
      onSubmit={async (values) => {
        try {
          await saveCreditAgreementFullArtifactWip({
            variables: {
              creditAgreementFullArtifactWip: {
                ...values,
                creditAgreement: JSON.stringify(values), // Serialize the full artifact
              },
            },
          });
          toast({
            title: 'Credit agreement WIP saved successfully',
            status: 'success',
          });
        } catch (err: any) {
          toast({
            ...errorToastConfig,
            title: getUnexpectedErrorMessage(err),
          });
        }
      }}
    >
      {(
        formProps: FormikProps<CreditAgreement.DomainEntity.CreditAgreementFullArtifact>,
      ) => {
        const isWipDirty = isWipDirtyCallback(formProps.values);
        const artifactDocumentId =
          creditAgreementFullArtifactWip.borrowingBaseMechanics
            .calcAgentMechanics._artifactDocumentId_ || null;

        return (
          <Form onSubmit={formProps.handleSubmit}>
            <CreditAgreementContextProvider
              initOrgSlugs={organizations.map(({ slug }) => slug)}
              parties={formProps.values.facilityMechanics.parties}
            >
              <CreditAgreementWipHeader
                isWipDirty={isWipDirty}
                isDiffOpen={isDiffOpen}
                setIsDiffOpen={setIsDiffOpen}
                creditFacilityId={creditFacilityId}
                formProps={formProps}
                artifactDocumentId={artifactDocumentId}
                currentCreditAgreementFullArtifact={
                  currentCreditAgreementFullArtifact
                }
                isJsonUploadOpen={isJsonUploadOpen}
                setIsJsonUploadOpen={setIsJsonUploadOpen}
                operationMode="client"
                saveCreditAgreementFullArtifactWip={
                  saveCreditAgreementFullArtifactWip
                }
                isSaveCreditAgreementFullArtifactWipLoading={
                  isSaveCreditAgreementFullArtifactWipLoading
                }
                onCommitDone={onCommitDone}
                onRemoveWipSuccess={() =>
                  navigate(
                    `/implementations/${implementationId}/credit-facilities/${creditFacilityId}/credit-agreements`,
                  )
                }
              />
              <CreditAgreementFullArtifactWithIntrospectionEntityFetch
                organizationId={organizationId}
                creditAgreementFullArtifact={formProps.values}
                organizationMap={organizationMap}
                tAccounts={tAccounts}
                facilityType={facilityType}
                formProps={formProps}
                creditFacilityId={creditFacilityId}
                implementationId={implementationId}
                arePartiesLocked={arePartiesLocked}
                setArePartiesLocked={setArePartiesLocked}
                loanProductsMap={loanProductsMap}
                // this is admin because we want the same functionality as admin version
                // operation mode `client` is used to conditional render "implementation portal" items
                operationMode="admin"
                saveCreditAgreementFullArtifactWip={
                  saveCreditAgreementFullArtifactWip
                }
                isSaveCreditAgreementFullArtifactWipLoading={
                  isSaveCreditAgreementFullArtifactWipLoading
                }
                saveAndUpdateConfigCreditAgreementWip={async (inputs) => {
                  // For client introspection, we don't need to update config
                  // Just save the current WIP state with the updated property
                  const currentValues = inputs.wipConfig.formProps.values;
                  await saveCreditAgreementFullArtifactWip({
                    variables: {
                      creditAgreementFullArtifactWip: {
                        ...currentValues,
                        creditAgreement: JSON.stringify(currentValues),
                      },
                    },
                  });
                }}
                bankAccountsWithInstitutionAndOwner={
                  bankAccountsWithInstitutionsData?.implementation
                    .bankAccountsWithInstitutionAndOwner
                }
              />
            </CreditAgreementContextProvider>
          </Form>
        );
      }}
    </Formik>
  );
};

// Main WIP wrapper component
const CreditAgreementWipIntrospection = ({
  organizationId,
  creditFacilityId,
}: {
  organizationId: string;
  creditFacilityId: string;
}) => {
  const params = useParams();
  const { implementationId } = RouteParamsStruct.parse(params);

  const {
    creditAgreementFullArtifactWip,
    currentCreditAgreementFullArtifact,
  } = useGetCurrentCaAndWip({
    creditFacilityId,
    creditAgreementsPath: `/implementations/${implementationId}/credit-facilities/${creditFacilityId}/credit-agreements`,
  });

  if (!creditAgreementFullArtifactWip || !currentCreditAgreementFullArtifact) {
    return <Skeleton height="150px" />;
  }

  return (
    <CreditAgreementDocumentWipIntrospection
      creditAgreementFullArtifactWip={creditAgreementFullArtifactWip}
      currentCreditAgreementFullArtifact={currentCreditAgreementFullArtifact}
      organizationId={organizationId}
      creditFacilityId={creditFacilityId}
    />
  );
};

// Fragments
const RouteBase = () => {
  const { user } = useUserAuthOrThrow();
  const params = useParams();
  const { creditFacilityId } = RouteParamsStruct.parse(params);

  return (
    <CreditAgreementWipIntrospection
      organizationId={user.organization.id}
      creditFacilityId={creditFacilityId}
    />
  );
};

// Exports
export const Route = () => {
  return (
    <RouteErrorBoundary
      fallback={({ message, showErrorToast }) => {
        showErrorToast(message);
        return <Navigate to="/" />;
      }}
    >
      <Flex
        direction="column"
        padding={['1.5rem', '1.5rem', '1.5rem', '2.5rem']}
      >
        <CreditAgreementsViewTemplate>
          <RouteBase />
        </CreditAgreementsViewTemplate>
      </Flex>
    </RouteErrorBoundary>
  );
};
